import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, isTaskOverdue } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { TasksTable } from '../tasks/TasksPage';
import { useCounters, counterVal } from '../../core/useCounters';


function ManagerDashboard() {
    const { clients, tasks, employees, invoices, user, setView } = useApp();
    const [drill, setDrill] = useState(null);
    const counters = useCounters();
    const now = new Date();

    // Manager sees their own team's clients
    const myEmps = employees.filter(e => e.id === user.id || true); // manager sees all
    const myClients = clients;
    const myTasks = tasks;

    const overdue = myTasks.filter(t => t.dueDate && new Date(t.dueDate) < now && t.status !== "completed");
    const aged12 = overdue.filter(t => (now - new Date(t.dueDate)) / (1000 * 3600) < 24);
    const aged24 = overdue.filter(t => { const h = (now - new Date(t.dueDate)) / (1000 * 3600); return h >= 24 && h < 48; });
    const aged48 = overdue.filter(t => { const h = (now - new Date(t.dueDate)) / (1000 * 3600); return h >= 48 && h < 72; });
    const aged72p = overdue.filter(t => (now - new Date(t.dueDate)) / (1000 * 3600) >= 72);

    const pendingReview = tasks.filter(t => t.status === "team_approval");
    const clientAction = tasks.filter(t => t.status === "client_action");

    return (
        <>
            {/* Overdue ageing — main focus for manager */}
            <div className="card card-gold" style={{ marginBottom: 20 }}>
                <div className="card-head">
                    <div className="card-title"><InlineIcon name="warning" size={18} gap={6}>Overdue Tasks — Ageing Analysis</InlineIcon></div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>{overdue.length} total overdue</div>
                </div>
                <div className="card-body">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 16 }}>
                        {[
                            ["< 24 Hours", aged12.length, "#FFF8EC", "#B45309", "Low Risk"],
                            ["24–48 Hours", aged24.length, "#FEF2F2", "#DC2626", "Needs Action"],
                            ["48–72 Hours", aged48.length, "#FFF1F2", "#BE123C", "Urgent"],
                            ["72 Hours +", aged72p.length, "#FDF2F8", "#9D174D", "Critical"],
                        ].map(([l, v, bg, c, tag]) => (
                            <div key={l} style={{ padding: "16px", borderRadius: 12, background: bg, border: `1.5px solid ${c}30`, cursor: "pointer", transition: ".2s" }}
                                onClick={() => setDrill({
                                    list: overdue.filter(t => {
                                        const h = (now - new Date(t.dueDate)) / (1000 * 3600);
                                        if (l === "< 24 Hours") return h < 24;
                                        if (l === "24–48 Hours") return h >= 24 && h < 48;
                                        if (l === "48–72 Hours") return h >= 48 && h < 72;
                                        return h >= 72;
                                    }), title: l + " Overdue"
                                })}>
                                <div style={{ fontSize: 28, fontWeight: 700, color: c, fontFamily: "'Outfit',sans-serif" }}>{v}</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: c, marginTop: 2 }}>{l}</div>
                                <div style={{ fontSize: 10, color: c, opacity: .7, marginTop: 2 }}>{tag}</div>
                            </div>
                        ))}
                    </div>
                    {overdue.length === 0 && (
                        <div style={{ textAlign: "center", padding: "20px", color: "var(--green)", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><Icon name="check" size={18} /> No overdue tasks! Great work by the team.</div>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="stat-cards dashboard-stat-cards" style={{ marginBottom: 20 }}>
                {[
                    { id: "clients", icon: IconProperties.clients, label: "Active Clients", val: counterVal(counters, 'active_clients'), color: "var(--blue)", note: counterVal(counters, 'total_clients', (n) => `${n} total`) },
                    { id: "review", icon: IconProperties.checkCircle, label: "Pending Review", val: pendingReview.length, color: "#7C3AED", note: "Submissions awaiting review" },
                    { id: "client_a", icon: IconProperties.spreadsheet, label: "Client Action Due", val: clientAction.length, color: "var(--orange)", note: "Waiting for client" },
                    { id: "overdue", icon: IconProperties.bell, label: "Overdue", val: counterVal(counters, 'overdue_tasks'), color: "var(--red)", note: `${aged72p.length} critical (72hr+)` },
                ].map(s => {
                    const IconComp = s.icon;
                    return (
                        <div key={s.id} className="stat-box dashboard-stat-card" onClick={() => setDrill({
                            list: s.id === "review" ? pendingReview : s.id === "client_a" ? clientAction : s.id === "overdue" ? overdue : s.id === "clients" ? myClients.filter(c => c.status === "active") : null,
                            title: s.label,
                            mode: s.id === "clients" ? "clients" : "tasks",
                        })}>
                            <div className="stat-icon dashboard-stat-icon" style={{ color: s.color, background: `${s.color}15` }}>
                                {IconComp && <IconComp size={18} strokeWidth={2} />}
                            </div>
                            <div className="stat-body">
                                <div className="stat-lbl">{s.label}</div>
                                <div className="stat-val" style={{ color: s.color }}>{s.val}</div>
                                <div className="stat-note">{s.note}</div>
                                <div className="stat-drill">Click to expand →</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Team performance */}
            <div className="grid2">
                <div className="card">
                    <div className="card-head">
                        <div className="card-title">Team Performance</div>
                        <button className="btn btn-sm" onClick={() => setView("analytics")}>Full Analytics →</button>
                    </div>
                    {employees.map(emp => {
                        const empTasks = tasks.filter(t => t.assignedTo === emp.id);
                        const empOverdue = empTasks.filter(t => t.dueDate && new Date(t.dueDate) < now && t.status !== "completed");
                        const empCompleted = empTasks.filter(t => t.status === "completed").length;
                        const pct = empTasks.length ? Math.round(empCompleted / empTasks.length * 100) : 0;
                        return (
                            <div key={emp.id} className="row-item">
                                <div className="av av-md" style={{ background: emp.color }}>{emp.avatar}</div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{emp.name}</div>
                                    <div style={{ fontSize: 11, color: "var(--muted)" }}>{empCompleted}/{empTasks.length} done · {empOverdue.length} overdue</div>
                                    <div className="prog-bg" style={{ marginTop: 5, maxWidth: 160 }}>
                                        <div className="prog-fill" style={{ width: pct + "%", background: pct >= 80 ? "var(--green)" : pct >= 50 ? "var(--gold)" : "var(--red)" }} />
                                    </div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: pct >= 80 ? "var(--green)" : pct >= 50 ? "var(--gold-dk)" : "var(--red)" }}>{pct}%</div>
                            </div>
                        );
                    })}
                </div>

                <div className="card">
                    <div className="card-head">
                        <div className="card-title">Recent Activity</div>
                    </div>
                    {tasks.filter(t => t.status === "team_approval").slice(0, 5).map(t => (
                        <div key={t.id} className="row-item">
                            <span style={{ fontSize: 18 }}><Icon name="review" size={18} /></span>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 500 }}>{t.title}</div>
                                <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.clientName}</div>
                            </div>
                            <span className="badge" style={{ background: "#F5F3FF", color: "#7C3AED" }}>Review</span>
                        </div>
                    ))}
                    {tasks.filter(t => t.status === "team_approval").length === 0 && (
                        <div style={{ padding: "20px", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>No pending reviews</div>
                    )}
                </div>
            </div>

            {/* Drill-down panel */}
            {drill?.list && (
                <div className="drill-overlay" onClick={e => e.target === e.currentTarget && setDrill(null)}>
                    <div className="drill-panel">
                        <div className="drill-head">
                            <div className="drill-title">{drill.title}</div>
                            <button onClick={() => setDrill(null)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--muted)" }}><Icon name="close" size={20} /></button>
                        </div>
                        <div className="drill-body">
                            {drill.mode === "clients" ? (
                                <div className="tbl-wrap">
                                    <table>
                                        <thead><tr><th>Client</th><th>Contact</th><th>Status</th><th>Outstanding</th></tr></thead>
                                        <tbody>
                                            {drill.list.map(c => (
                                                <tr key={c.id}>
                                                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                                                    <td style={{ fontSize: 12 }}>{c.email}</td>
                                                    <td><Badge status={c.status === "active" ? "completed" : "open"} label={c.status} /></td>
                                                    <td style={{ fontWeight: 700, color: c.pending > 0 ? "var(--red)" : "var(--muted)" }}>{INR(c.pending)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <TasksTable tasks={drill.list} showClient={true} />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════════
// CROSS-SELL TRIGGER + FREE GIFTS + MARKETPLACE
// ═══════════════════════════════════════════════════════════════════════

// Free gifts/resources available for download
const FREE_GIFTS = [
    {
        id: "g1", name: "Startup Kit 2025",
        desc: "Complete startup guide — legal checklist, tax calendar, compliance tracker",
        iconKey: "gifts", format: "PDF", pages: 24, triggerOn: "registration_complete",
        downloadUrl: "#", color: "var(--gold)",
    },
    {
        id: "g2", name: "GST Compliance Calendar",
        desc: "Month-by-month GST filing calendar with due dates and penalties",
        iconKey: "spreadsheet", format: "PDF", pages: 8, triggerOn: "gst_registered",
        downloadUrl: "#", color: "var(--blue)",
    },
    {
        id: "g3", name: "Director KYC Checklist",
        desc: "Complete KYC document checklist for all directors",
        iconKey: "checkCircle", format: "PDF", pages: 4, triggerOn: "always",
        downloadUrl: "#", color: "var(--green)",
    },
    {
        id: "g4", name: "Startup India Guide",
        desc: "Step-by-step guide to Startup India recognition and benefits",
        iconKey: "dashboard", format: "PDF", pages: 16, triggerOn: "registration_complete",
        downloadUrl: "#", color: "#7C3AED",
    },
];

// Marketplace services
const MARKETPLACE_SERVICES = [
    { id: "ms1", category: "GST", iconKey: "analytics", name: "GST Registration", price: 2500, desc: "GSTIN registration for your business" },
    { id: "ms2", category: "GST", iconKey: "analytics", name: "GST Return Filing (Annual)", price: 12000, desc: "All GST returns for 12 months" },
    { id: "ms3", category: "Compliance", iconKey: "organisation", name: "Annual ROC Compliance", price: 8000, desc: "Annual return filing with MCA" },
    { id: "ms4", category: "Compliance", iconKey: "organisation", name: "Annual Audit", price: 15000, desc: "Statutory audit by CA" },
    { id: "ms5", category: "Tax", iconKey: "billingSummary", name: "Income Tax Return (Co)", price: 6000, desc: "Corporate ITR filing" },
    { id: "ms6", category: "Tax", iconKey: "billingSummary", name: "TDS Returns (Quarterly)", price: 4000, desc: "4 quarters TDS filing" },
    { id: "ms7", category: "Trademark", iconKey: "templates", name: "Trademark Registration", price: 8500, desc: "Class 1 trademark application" },
    { id: "ms8", category: "Startup India", iconKey: "dashboard", name: "Startup India Recognition", price: 5000, desc: "DPIIT recognition certificate" },
    { id: "ms9", category: "MSME", iconKey: "bundles", name: "MSME/Udyam Registration", price: 1500, desc: "Udyam certificate" },
    { id: "ms10", category: "Payroll", iconKey: "employees", name: "Payroll Processing (Monthly)", price: 2000, desc: "Monthly payroll & compliance" },
];




export { ManagerDashboard };